import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface BookingStateItem {
  roomId: number;
  propertyId: number;
  checkIn: string;
  checkOut: string;
  timestamp: number;
}

export interface BookingState {
  reservations: BookingStateItem[];
  shortlist: BookingStateItem[];
}

const initialState: BookingState = {
  reservations: [],
  shortlist: [],
};

const bookingStateSlice = createSlice({
  name: 'bookingState',
  initialState,
  reducers: {
    // Reservation actions
    addReservation: (state, action: PayloadAction<BookingStateItem>) => {
      // Remove from reservations if already exists
      state.reservations = state.reservations.filter(
        item => item.roomId !== action.payload.roomId
      );
      // Add new reservation
      state.reservations.push(action.payload);
      
      // Remove from shortlist if it exists (a room cannot be both reserved and shortlisted)
      state.shortlist = state.shortlist.filter(
        item => item.roomId !== action.payload.roomId
      );
    },
    removeReservation: (state, action: PayloadAction<number>) => {
      state.reservations = state.reservations.filter(
        item => item.roomId !== action.payload
      );
    },
    clearReservations: (state) => {
      state.reservations = [];
    },

    // Shortlist actions
    addToShortlist: (state, action: PayloadAction<BookingStateItem>) => {
      // Remove from shortlist if already exists
      state.shortlist = state.shortlist.filter(
        item => item.roomId !== action.payload.roomId
      );
      // Add to shortlist
      state.shortlist.push(action.payload);
      
      // Remove from reservations if it exists (a room cannot be both reserved and shortlisted)
      state.reservations = state.reservations.filter(
        item => item.roomId !== action.payload.roomId
      );
    },
    removeFromShortlist: (state, action: PayloadAction<number>) => {
      state.shortlist = state.shortlist.filter(
        item => item.roomId !== action.payload
      );
    },
    clearShortlist: (state) => {
      state.shortlist = [];
    },

    // Bulk actions
    clearAllBookingState: (state) => {
      state.reservations = [];
      state.shortlist = [];
    },

    // Initialize state with fresh data (clears existing and sets new)
    initializeReservations: (state, action: PayloadAction<BookingStateItem[]>) => {
      state.reservations = action.payload;
    },

    initializeShortlist: (state, action: PayloadAction<BookingStateItem[]>) => {
      state.shortlist = action.payload;
    },
  },
});

export const {
  addReservation,
  removeReservation,
  clearReservations,
  addToShortlist,
  removeFromShortlist,
  clearShortlist,
  clearAllBookingState,
  initializeReservations,
  initializeShortlist,
} = bookingStateSlice.actions;

// Selectors
export const selectReservations = (state: { bookingState: BookingState }) => state.bookingState.reservations;
export const selectShortlist = (state: { bookingState: BookingState }) => state.bookingState.shortlist;

export const selectIsReserved = (roomId: number) => (state: { bookingState: BookingState }) =>
  state.bookingState.reservations.some(item => item.roomId === roomId);

export const selectIsShortlisted = (roomId: number) => (state: { bookingState: BookingState }) =>
  state.bookingState.shortlist.some(item => item.roomId === roomId);

export const selectReservationByRoomId = (roomId: number) => (state: { bookingState: BookingState }) =>
  state.bookingState.reservations.find(item => item.roomId === roomId);

export const selectShortlistByRoomId = (roomId: number) => (state: { bookingState: BookingState }) =>
  state.bookingState.shortlist.find(item => item.roomId === roomId);

export default bookingStateSlice.reducer;